import { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, TrendingDown, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  TooltipProps,
} from "recharts";
import {
  TAB_IDS,
  type AgeRow,
  type TabId,
  type GenderOrDeviceRow,
  type RegionRow,
  type TopLocations,
} from "../../types/Tabs";
import {
  DEFAULT_TOP_LOCATIONS_DISPLAY_COUNT,
  MAX_TOP_LOCATIONS,
  useTabsData,
} from "../../hooks/UseTabsData";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import DefinitionTooltip, { type DefinitionTooltipInfo } from "src/components/common/DefinitionTooltip";
import { CHART_COLOR_PALETTE } from "src/components/charts/ChartPalette";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import {
  formatAgeBucketLabel,
  formatDimensionValue,
  formatMetricPercent,
  formatMetricValue,
  formatSignedMetricPercent,
} from "../../utils/WebAnalyticsFormat";
import {
  isArabicLanguage,
  toArabicIndicDigitsInString,
  toWesternDigitsInString,
} from "src/utils/numberFormat";
import "./TabsDashboard.scss";

const WEB_ANALYTICS_PRIMARY_CHART_COLOR = CHART_COLOR_PALETTE[0];
const LOCATION_BAR_COLORS = ["#2f6eea", "#34cdbd", "#a855f7", "#60a5fa", "#10b981", "#f59e0b"];

const getLocationPopupContainer = (trigger: HTMLElement): HTMLElement =>
  trigger.closest<HTMLElement>(".top-locations__controls") ?? document.body;

const TAB_TOOLTIP_FORMULA_KEYS: Record<TabId, string> = {
  users: "totalUsers",
  sessions: "totalSessions",
  pageviews: "totalPageviews",
  "session-duration": "avgSessionDuration",
  "bounce-rate": "avgBounceRate",
  "session-user": "avgSessionsPerUser",
};

const TAB_CITY_TOOLTIP_KEYS: Record<TabId, string> = {
  users: "users",
  sessions: "sessions",
  pageviews: "pageviews",
  "session-duration": "sessionDuration",
  "bounce-rate": "bounceRate",
  "session-user": "sessionsPerUser",
};

const TAB_CHANNEL_TOOLTIP_KEYS = TAB_CITY_TOOLTIP_KEYS;

type DemographicDimension = "age" | "gender" | "device";
type MetricTooltipProps = TooltipProps<number, string> & {
  metricLabel: string;
  activeTab: TabId;
};

/** Whether the top-locations panel ranks individual cities or aggregates them by country. */
type LocationMode = "city" | "country";

type LocationChartRow = {
  /** Full label used for tooltips/aria (e.g. "Karachi, Pakistan"). */
  name: string;
  /** Short label rendered in the row (city name, or country name in country mode). */
  label: string;
  value: number;
};

function normalizeLocationText(value: unknown): string {
  if (Array.isArray(value)) {
    return normalizeLocationText(value[0]);
  }
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The backend already groups and ranks locations by city or country (`topLocations.city` /
 * `topLocations.country`), so this only maps each row to its display label — no client-side
 * aggregation or "(not set)" bucketing is needed. Re-sorts defensively in case of ties/gaps.
 */
function buildLocationRows(rows: RegionRow[], mode: LocationMode): LocationChartRow[] {
  return rows
    .map(row => {
      if (mode === "country") {
        const label = normalizeLocationText(row.country) || normalizeLocationText(row.name);
        return { name: label, label, value: row.value };
      }
      const city = normalizeLocationText(row.city) || normalizeLocationText(row.name);
      const country = normalizeLocationText(row.country);
      return {
        name: country && country !== city ? `${city}, ${country}` : city,
        label: city,
        value: row.value,
      };
    })
    .sort((a, b) => b.value - a.value);
}

function normalizeTrendPercent(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function hasFiniteMetricValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function hasNonZeroMetricValue(value: unknown): value is number {
  return hasFiniteMetricValue(value) && Math.abs(value) > 0;
}

function getRowsWithMetricValues<T extends { value?: unknown }>(rows: readonly T[]): T[] {
  return rows.filter(row => hasFiniteMetricValue(row.value));
}

function getRowsWithMetricAndPercentValues<T extends { value?: unknown; percent?: unknown }>(
  rows: readonly T[]
): T[] {
  return rows.filter(row => hasFiniteMetricValue(row.value) && hasFiniteMetricValue(row.percent));
}

function hasNonZeroRows<T extends { value?: unknown }>(rows: readonly T[]): boolean {
  return rows.some(row => hasNonZeroMetricValue(row.value));
}

const PieTooltip: FC<MetricTooltipProps> = ({ active, payload, metricLabel, activeTab }) => {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }
  const p = payload[0];
  const name = formatDimensionValue("gender", String(p.name ?? ""), t);
  const value = p.value ?? 0;
  const percent = (p.payload as GenderOrDeviceRow)?.percent ?? 0;
  return (
    <div className="analytics-chart-tooltip">
      <div className="analytics-chart-tooltip__bar" />
      <div className="analytics-chart-tooltip__content">
        <span className="analytics-chart-tooltip__label">{name}</span>
        <span className="analytics-chart-tooltip__value">
          {metricLabel}: {formatMetricValue(activeTab, Number(value), t, i18n.language)} (
          {formatMetricPercent(percent, i18n.language)})
        </span>
      </div>
    </div>
  );
};

const DevicePieTooltip: FC<MetricTooltipProps> = ({
  active,
  payload,
  metricLabel,
  activeTab,
}) => {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }
  const p = payload[0];
  const name = formatDimensionValue("device", String(p.name ?? ""), t);
  const value = p.value ?? 0;
  return (
    <div className="analytics-chart-tooltip analytics-chart-tooltip--device">
      <div className="analytics-chart-tooltip__bar" />
      <div className="analytics-chart-tooltip__content">
        <span className="analytics-chart-tooltip__label">
          {t("webAnalytics.tooltips.deviceCategory")} <strong>{name}</strong>
        </span>
        <span className="analytics-chart-tooltip__value">
          {metricLabel}: {formatMetricValue(activeTab, Number(value), t, i18n.language)}
        </span>
      </div>
    </div>
  );
};

const ChannelRow: FC<{
  trendData: number[];
  name: string;
  value: number;
  changePercent: number;
  maxValue: number;
  minValue: number;
  metricLabel: string;
  startMonthLabel: string;
  endMonthLabel: string;
  activeTab: TabId;
}> = ({
  trendData,
  name,
  value,
  changePercent,
  maxValue,
  minValue,
  metricLabel,
  startMonthLabel,
  endMonthLabel,
  activeTab,
}) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const signedChangePercent = formatSignedMetricPercent(changePercent, language);
    const isIncrease = changePercent >= 0;
    const isBounceRate = activeTab === "bounce-rate";
    const isPositiveOutcome = isBounceRate ? changePercent <= 0 : isIncrease;
    const directionLabel = isIncrease
      ? t("webAnalytics.tooltips.increase", { defaultValue: "increase" })
      : t("webAnalytics.tooltips.decrease", { defaultValue: "decrease" });
    const bounceOutcomeLabel = isPositiveOutcome
      ? t("webAnalytics.tooltips.bounceRateDecreasePositive", {
          defaultValue: "Decrease is positive for bounce rate",
        })
      : t("webAnalytics.tooltips.bounceRateIncreaseNegative", {
          defaultValue: "Increase is negative for bounce rate",
        });
    const changeAccessibilityLabel = isBounceRate
      ? `${signedChangePercent} ${directionLabel}. ${bounceOutcomeLabel}.`
      : `${signedChangePercent} ${directionLabel}.`;
    const [isBarHovered, setIsBarHovered] = useState(false);
    const baselineValue = trendData.length >= 1 ? trendData[0] ?? null : null;
    const comparisonValue = trendData.length > 1 ? trendData.at(-1) ?? null : null;

    const barPercent = maxValue > 0 ? calculateBarWidthPct(value, maxValue, minValue) : 0;

    return (
      <div className="channel-row">
        <span className="channel-row__name">{name}</span>
        <section
          className="channel-row__value-wrap"
          aria-label={name}
          onMouseEnter={() => setIsBarHovered(true)}
          onMouseLeave={() => setIsBarHovered(false)}
        >
          <div className="channel-row__bar">
            <span className="channel-row__bar-fill" style={{ width: `${barPercent}%` }} />
            {isBarHovered && (
              <div className="channel-row__bar-tooltip kpi-graph-tooltip" role="tooltip">
                <div className="kpi-graph-tooltip__content">
                  <div className="kpi-graph-tooltip__title">
                    {t("webAnalytics.tooltips.changeInMetric", {
                      defaultValue: "Change In {{metric}}",
                      metric: metricLabel,
                    })}
                  </div>
                  {(baselineValue !== null || comparisonValue !== null) && (
                    <div className="kpi-graph-tooltip__comparison">
                      <div className="kpi-graph-tooltip__metric">
                        <strong>
                          {baselineValue !== null
                            ? formatMetricValue(activeTab, baselineValue, t, language)
                            : t("webAnalytics.tooltips.notAvailable")}
                        </strong>
                        <span>{startMonthLabel}</span>
                      </div>
                      <span className="kpi-graph-tooltip__vs">
                        {t("webAnalytics.tooltips.vs")}
                      </span>
                      <div className="kpi-graph-tooltip__metric">
                        <strong>
                          {comparisonValue !== null
                            ? formatMetricValue(activeTab, comparisonValue, t, language)
                            : t("webAnalytics.tooltips.notAvailable")}
                        </strong>
                        <span>{endMonthLabel}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
        <span
          className={`channel-row__change ${
            isPositiveOutcome ? "channel-row__change--pos" : "channel-row__change--neg"
          }`}
          aria-label={changeAccessibilityLabel}
          title={isBounceRate ? bounceOutcomeLabel : changeAccessibilityLabel}
        >
          {changePercent === 0 ? (
            "-"
          ) : (
            <>
              {isIncrease ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}{" "}
              {signedChangePercent}
            </>
          )}
        </span>
      </div>
    );
  };

const PIE_COLORS = ["#2563eb", "#6ee7b7", "#fdba74", "#111827", "#a855f7", "#60a5fa"] as const;

/** Tabs whose per-slice values add up to a meaningful total shown in the donut hole. */
const SUMMABLE_TABS: readonly TabId[] = ["users", "sessions", "pageviews"];

const AGE_UNKNOWN_ORDER = -1;
const AGE_RANGE_REGEX = /^(\d+)\s*-\s*\d+$/;
const AGE_PLUS_REGEX = /^(\d+)\s*\+$/;

function getAgeSortOrder(label: string): number {
  const normalized = String(label).trim().toLowerCase();
  if (normalized === "unknown" || normalized === "(not set)" || normalized === "not set") {
    return AGE_UNKNOWN_ORDER;
  }

  const rangeMatch = AGE_RANGE_REGEX.exec(normalized);
  if (rangeMatch) {
    return Number(rangeMatch[1]);
  }

  const plusMatch = AGE_PLUS_REGEX.exec(normalized);
  if (plusMatch) {
    return Number(plusMatch[1]);
  }

  return Number.MAX_SAFE_INTEGER;
}

type DonutWithLegendProps = Readonly<{
  data: GenderOrDeviceRow[];
  colors?: readonly string[];
  TooltipComponent: React.ComponentType<MetricTooltipProps>;
  metricLabel: string;
  activeTab: TabId;
  dimension: Extract<DemographicDimension, "gender" | "device">;
  legendClassName?: string;
}>;

/** Donut with the total in its hole and a single-column legend below. */
function DonutWithLegend({
  data,
  colors = PIE_COLORS,
  TooltipComponent,
  metricLabel,
  activeTab,
  dimension,
  legendClassName = "",
}: DonutWithLegendProps) {
  const { t, i18n } = useTranslation();
  const total = useMemo(() => data.reduce((sum, row) => sum + row.value, 0), [data]);
  return (
    <div className="donut-with-legend">
      <div className="donut-with-legend__chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={<TooltipComponent metricLabel={metricLabel} activeTab={activeTab} />}
              allowEscapeViewBox={{ x: true, y: true }}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="54%"
              outerRadius="86%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={0}
              stroke="#ffffff"
              strokeWidth={2}
            >
              {data.map((row, i) => (
                <Cell key={row.name} fill={colors[i % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {SUMMABLE_TABS.includes(activeTab) && (
          <div className="donut-with-legend__center">
            <span className="donut-with-legend__total">
              {formatMetricValue(activeTab, total, t, i18n.language)}
            </span>
            <span className="donut-with-legend__total-label">
              {t("webAnalytics.donut.total", {
                metric: metricLabel,
                defaultValue: "Total {{metric}}",
              })}
            </span>
          </div>
        )}
      </div>
      <ul className={`donut-legend ${legendClassName}`.trim()} aria-hidden>
        {data.map((row, i) => (
          <li key={row.name} className="donut-legend__item">
            <span className="donut-legend__swatch" style={{ backgroundColor: colors[i % colors.length] }} />
            <span className="donut-legend__name">
              {formatDimensionValue(dimension, String(row.name), t)}
            </span>
            <strong className="donut-legend__stats">
              {formatMetricValue(activeTab, row.value, t, i18n.language)} (
              {formatMetricPercent(row.percent, i18n.language)})
            </strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

const WebAnalyticsUnavailableState: FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation();
  return (
    <div className={`web-analytics-unavailable ${className}`.trim()}>
      <strong>
        {t("webAnalytics.unavailable.title", { defaultValue: "Data Not Available" })}
      </strong>
      <span>
        {t("webAnalytics.unavailable.description", {
          defaultValue: "we do not have enough data",
        })}
      </span>
    </div>
  );
};

const TopLocationsPanel: FC<{
  topLocations: TopLocations;
  metricLabel: string;
  activeTab: TabId;
}> = ({ topLocations, metricLabel, activeTab }) => {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<LocationMode>("city");
  const [selectedLocations, setSelectedLocations] = useState<string[] | null>(null);
  const [displayCount, setDisplayCount] = useState<number>(DEFAULT_TOP_LOCATIONS_DISPLAY_COUNT);
  // Decoupled from `displayCount` so the field can sit empty (or mid-edit) while typing —
  // binding the input straight to a number would force it back to the clamped value (e.g. "1")
  // on every keystroke, making it impossible to clear and retype.
  const [displayCountInput, setDisplayCountInput] = useState<string>(
    String(DEFAULT_TOP_LOCATIONS_DISPLAY_COUNT)
  );

  const title =
    mode === "country"
      ? t("webAnalytics.sections.topCountriesByMetric", {
          metric: metricLabel,
          defaultValue: "Top Countries by {{metric}}",
        })
      : t("webAnalytics.sections.topCitiesByMetric", {
          metric: metricLabel,
          defaultValue: "Top Cities by {{metric}}",
        });
  const tooltipNamespace = mode === "country" ? "countryTooltips" : "cityTooltips";
  const locationTooltip = useMemo<DefinitionTooltipInfo>(
    () => ({
      description: t(
        `webAnalytics.${tooltipNamespace}.${TAB_CITY_TOOLTIP_KEYS[activeTab]}.description`
      ),
      formula: t(`webAnalytics.${tooltipNamespace}.${TAB_CITY_TOOLTIP_KEYS[activeTab]}.formula`),
    }),
    [activeTab, t, tooltipNamespace]
  );

  const rankedRows = useMemo(
    () => getRowsWithMetricValues(buildLocationRows(topLocations[mode], mode)),
    [mode, topLocations]
  );
  const hasLocationValues = hasNonZeroRows(rankedRows);
  // rankedRows is already sorted descending, so slicing from the front always keeps row 0
  // (the max) in view — the dropdown only needs to cap how many rows render, not re-rank them.
  const locationOptions = useMemo(
    () => rankedRows.map(row => ({ name: row.name, displayName: row.label })),
    [rankedRows]
  );
  const allLocationNames = useMemo(() => rankedRows.map(row => row.name), [rankedRows]);
  const selectedNames = useMemo(
    () => selectedLocations?.filter(name => allLocationNames.includes(name)) ?? allLocationNames,
    [allLocationNames, selectedLocations]
  );
  const visibleRows = useMemo(
    () =>
      rankedRows
        .filter(row => selectedNames.length === 0 || selectedNames.includes(row.name))
        .slice(0, displayCount),
    [rankedRows, selectedNames, displayCount]
  );

  const maxValue = useMemo(
    () => (visibleRows.length > 0 ? Math.max(...visibleRows.map(row => row.value), 1) : 1),
    [visibleRows]
  );
  const minValue = useMemo(
    () =>
      visibleRows.length > 0
        ? Math.min(...visibleRows.filter(row => row.value > 0).map(row => row.value), 1)
        : 1,
    [visibleRows]
  );

  return (
    <div className="top-locations">
      <div className="top-locations__header">
        <h3 className="demographics-subtitle demographics-subtitle--with-tooltip">
          <span>{title}</span>
          <DefinitionTooltip
            label={title}
            info={locationTooltip}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </h3>
        <div
          className="top-locations__segmented"
          role="group"
          aria-label={t("webAnalytics.sections.groupLocationsBy", {
            defaultValue: "Group locations by",
          })}
        >
          {(["city", "country"] as LocationMode[]).map(option => (
            <button
              key={option}
              type="button"
              className={`top-locations__segmented-button ${
                mode === option ? "top-locations__segmented-button--active" : ""
              }`.trim()}
              aria-pressed={mode === option}
              onClick={() => {
                setMode(option);
                setSelectedLocations(null);
              }}
            >
              {t(`webAnalytics.sections.${option}`, {
                defaultValue: option === "city" ? "City" : "Country",
              })}
            </button>
          ))}
        </div>
      </div>

      <div className="top-locations__controls">
        <AudienceListSelect
          className="top-locations__select"
          showOmittedTooltip={false}
          showSelectedIcon
          getPopupContainer={getLocationPopupContainer}
          popupClassName="top-locations__popup"
          value={selectedNames}
          options={locationOptions}
          onChange={setSelectedLocations}
        />
        <label className="top-locations__rows-per-page">
          <span>{t("webAnalytics.sections.show", { defaultValue: "Show" })}</span>
          <span className="top-locations__count-input">
            {/*
              Deliberately `type="text"`, not `type="number"` — number inputs have a
              well-known browser quirk where, while focused, the visible text can stay
              stuck on what was literally typed (e.g. "+5", "8-") even after the
              controlled `value` prop changes, because the browser tracks its own
              internal "raw"/invalid-input state separately from the exposed `.value`.
              A text input has no such split state, so sanitizing on every keystroke
              below reliably reaches the screen. `inputMode`/`pattern` still bring up a
              numeric keypad on mobile.
            */}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={
                isArabicLanguage(i18n.language)
                  ? toArabicIndicDigitsInString(displayCountInput)
                  : displayCountInput
              }
              onChange={event => {

                const raw = toWesternDigitsInString(event.target.value).replace(/[^0-9]/g, "");

                if (raw === "") {
                  setDisplayCountInput(raw);
                  return;
                }
                const parsed = Number(raw);
                const clamped = Math.min(MAX_TOP_LOCATIONS, Math.max(1, Math.round(parsed)));
                setDisplayCount(clamped);
                setDisplayCountInput(String(clamped));
              }}
              onBlur={() => {
                // Leaving the field empty/invalid should fall back to the last committed
                // count rather than staying blank.
                setDisplayCountInput(String(displayCount));
              }}
            />
            <span className="top-locations__count-steppers">
              <button
                type="button"
                className="top-locations__count-stepper-button"
                aria-label={t("webAnalytics.sections.increaseCount", {
                  defaultValue: "Increase count",
                })}
                disabled={displayCount >= MAX_TOP_LOCATIONS}
                onClick={() => {
                  const next = Math.min(MAX_TOP_LOCATIONS, displayCount + 1);
                  setDisplayCount(next);
                  setDisplayCountInput(String(next));
                }}
              >
                <ChevronUp size={10} aria-hidden />
              </button>
              <button
                type="button"
                className="top-locations__count-stepper-button"
                aria-label={t("webAnalytics.sections.decreaseCount", {
                  defaultValue: "Decrease count",
                })}
                disabled={displayCount <= 1}
                onClick={() => {
                  const next = Math.max(1, displayCount - 1);
                  setDisplayCount(next);
                  setDisplayCountInput(String(next));
                }}
              >
                <ChevronDown size={10} aria-hidden />
              </button>
            </span>
          </span>
        </label>

      </div>

      {!hasLocationValues ? (
        <WebAnalyticsUnavailableState className="web-analytics-unavailable--top-cities" />
      ) : (
        <div className="top-locations-list">
          {visibleRows.map((row, index) => {
            const barPercent = calculateBarWidthPct(row.value, maxValue, minValue);
            const color = LOCATION_BAR_COLORS[index % LOCATION_BAR_COLORS.length];

            return (
              <div className="top-locations-list__row" key={`${row.name}-${index}`}>
                <span className="top-locations-list__label" title={row.name}>
                  {row.label}
                </span>
                <span
                  className="top-locations-list__track"
                  aria-label={`${row.name}: ${formatMetricValue(activeTab, row.value, t, i18n.language)}`}
                >
                  <span
                    className="top-locations-list__fill"
                    style={{ width: `${barPercent}%`, backgroundColor: color }}
                  />
                </span>
                <strong className="top-locations-list__value">
                  {formatMetricValue(activeTab, row.value, t, i18n.language)}
                </strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AGE_AXIS_TICK_PERCENTS = [0, 50, 100];

/** Age distribution as label-above-bar rows with a shared 0-100% axis. */
const AgeBarList: FC<{
  ages: AgeRow[];
  metricLabel: string;
  activeTab: TabId;
}> = ({ ages, metricLabel, activeTab }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  return (
    <div className="demographics-age-list">
      {ages.map(row => {
        const label = formatAgeBucketLabel(row.name, t, language);
        const value = formatMetricValue(activeTab, row.value, t, language);
        const percent = formatMetricPercent(row.percent, language);
        const valueLabel = `${label}: ${value} (${percent})`;
        return (
          <div className="demographics-age-list__row" key={row.name}>
            <div className="demographics-age-list__header">
              <span className="demographics-age-list__label">{label}</span>
              <span className="demographics-age-list__stats">
                <span className="demographics-age-list__value">{value}</span>{" "}
                <span className="demographics-age-list__percent">({percent})</span>
              </span>
            </div>
            <span
              className="demographics-age-list__track"
              title={`${metricLabel} — ${valueLabel}`}
              aria-label={valueLabel}
            >
              <span
                className="demographics-age-list__fill"
                style={{
                  width: `${Math.min(Math.max(row.percent, 0), 100)}%`,
                  backgroundColor: WEB_ANALYTICS_PRIMARY_CHART_COLOR,
                }}
              />
            </span>
          </div>
        );
      })}
      <div className="demographics-age-list__axis" aria-hidden>
        {AGE_AXIS_TICK_PERCENTS.map(tick => (
          <span key={tick}>{formatMetricPercent(tick, language, 0)}</span>
        ))}
      </div>
    </div>
  );
};

const DemographicChartTitle: FC<{
  activeTab: TabId;
  dimension: DemographicDimension;
}> = ({ activeTab, dimension }) => {
  const { t } = useTranslation();
  const title = t(`webAnalytics.demographicChartTitles.${dimension}.${activeTab}`);
  const tooltip = useMemo<DefinitionTooltipInfo>(
    () => ({
      description: t(
        `webAnalytics.demographicTooltips.${TAB_CITY_TOOLTIP_KEYS[activeTab]}.description`,
        { group: t(`webAnalytics.demographicDimensions.${dimension}`) }
      ),
      formula: t(`webAnalytics.demographicTooltips.${TAB_CITY_TOOLTIP_KEYS[activeTab]}.formula`, {
        group: t(`webAnalytics.demographicDimensions.${dimension}`),
      }),
    }),
    [activeTab, dimension, t]
  );

  return (
    <h3 className="demographics-subtitle demographics-subtitle--with-tooltip">
      <span>{title}</span>
      <DefinitionTooltip
        label={title}
        info={tooltip}
        formulaTitle={t("webAnalytics.kpi.formula")}
      />
    </h3>
  );
};

interface TabsDashboardProps {
  startDate: string;
  endDate: string;
  /** When true (e.g. parent KPI loading on date change), hide this section's spinner to avoid duplicate */
  suppressSpinner?: boolean;
  /** Calendar month labels for channel tooltips (e.g. Arabic month names). */
  locale?: "en" | "ar";
  onNarrativeDataChange?: (_data: {
    tabKey: TabId;
    rawData: unknown;
    loading: boolean;
    error: string | null;
  }) => void;
}

const TabsDashboard: FC<TabsDashboardProps> = ({
  startDate,
  endDate,
  suppressSpinner = false,
  locale = "en",
  onNarrativeDataChange,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>("users");
  const { data, rawData, loading, error } = useTabsData(startDate, endDate, activeTab, locale);

  useEffect(() => {
    onNarrativeDataChange?.({
      tabKey: activeTab,
      rawData,
      loading,
      error,
    });
  }, [activeTab, error, loading, onNarrativeDataChange, rawData]);

  const metricLabel = t(`webAnalytics.tabs.${activeTab}`);
  const metricHeadingTooltip = useMemo<DefinitionTooltipInfo>(
    () => ({
      description: t(
        `webAnalytics.kpiFormulas.${TAB_TOOLTIP_FORMULA_KEYS[activeTab]}.description`
      ),
      formula: t(`webAnalytics.kpiFormulas.${TAB_TOOLTIP_FORMULA_KEYS[activeTab]}.formula`),
    }),
    [activeTab, t]
  );
  const channelTooltip = useMemo<DefinitionTooltipInfo>(
    () => ({
      description: t(
        `webAnalytics.channelTooltips.${TAB_CHANNEL_TOOLTIP_KEYS[activeTab]}.description`
      ),
      formula: t(
        `webAnalytics.channelTooltips.${TAB_CHANNEL_TOOLTIP_KEYS[activeTab]}.formula`
      ),
    }),
    [activeTab, t]
  );
  const channelTitle = t("webAnalytics.sections.channelByMetric", { metric: metricLabel });
  const startMonthLabel = useMemo(
    () => dayjs(startDate).locale(locale === "ar" ? "ar" : "en").format("MMMM, YYYY"),
    [startDate, locale]
  );
  const endMonthLabel = useMemo(
    () => dayjs(endDate).locale(locale === "ar" ? "ar" : "en").format("MMMM, YYYY"),
    [endDate, locale]
  );
  const sortedAges = useMemo(
    () =>
      [...data.demographics.ages].sort(
        (a, b) => getAgeSortOrder(a.name) - getAgeSortOrder(b.name)
      ),
    [data.demographics.ages]
  );

  const sortedChannels = useMemo(
    () => [...data.channels].sort((a, b) => b.value - a.value),
    [data.channels]
  );

  const sortedGender = useMemo(
    () => [...data.demographics.gender].sort((a, b) => b.value - a.value),
    [data.demographics.gender]
  );

  const sortedDevice = useMemo(
    () => [...data.demographics.device].sort((a, b) => b.value - a.value),
    [data.demographics.device]
  );

  const plottableAges = useMemo(() => getRowsWithMetricAndPercentValues(sortedAges), [sortedAges]);
  const plottableGender = useMemo(() => getRowsWithMetricAndPercentValues(sortedGender), [sortedGender]);
  const plottableDevice = useMemo(() => getRowsWithMetricAndPercentValues(sortedDevice), [sortedDevice]);
  const plottableChannels = useMemo(() => getRowsWithMetricValues(sortedChannels), [sortedChannels]);
  const channelMaxValue = useMemo(
    () =>
      plottableChannels.length > 0
        ? Math.max(...plottableChannels.map(ch => ch.value), 1)
        : 1,
    [plottableChannels]
  );
  const channelMinValue = useMemo(
    () =>
      plottableChannels.length > 0
        ? Math.min(...plottableChannels.filter(ch => ch.value > 0).map(ch => ch.value), 1)
        : 1,
    [plottableChannels]
  );
  const hasAgeValues = hasNonZeroRows(plottableAges);
  const hasGenderValues = hasNonZeroRows(plottableGender);
  const hasDeviceValues = hasNonZeroRows(plottableDevice);
  const hasChannelValues = hasNonZeroRows(plottableChannels);

  if (loading && !suppressSpinner) {
    return (
      <div className="web-analytics-tabs-dashboard web-analytics-tabs-dashboard--empty">
        <LoadingSpinner compact />
      </div>
    );
  }

  if (loading && suppressSpinner) {
    return (
      <div className="web-analytics-tabs-dashboard web-analytics-tabs-dashboard--empty web-analytics-tabs-dashboard--loading-suppressed" />
    );
  }

  if (error) {
    return (
      <div className="web-analytics-tabs-dashboard web-analytics-tabs-dashboard--empty">
        <div className="web-analytics-tabs-dashboard__no-data" title={error}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="web-analytics-tabs-dashboard">
      <div className="web-analytics-tabs-dashboard__tabs" data-pdf-block="tabs-strip">
        {TAB_IDS.map(id => (
          <button
            key={id}
            type="button"
            className={`web-analytics-tabs-dashboard__tab ${activeTab === id ? "web-analytics-tabs-dashboard__tab--active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
            {t(`webAnalytics.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div className="web-analytics-tabs-dashboard__card">
        <div data-pdf-block="tabs-content" data-pdf-splittable>
        <div className="web-analytics-tabs-dashboard__metric-heading">
          <span>{metricLabel}</span>
          <DefinitionTooltip
            label={metricLabel}
            info={metricHeadingTooltip}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
        <h2 className="web-analytics-tabs-dashboard__section-title">
          {t("webAnalytics.sections.demographicsByMetric", { metric: metricLabel })}
        </h2>

        <div className="web-analytics-tabs-dashboard__cols">
          {/* Top left: locations */}
          <div className="web-analytics-tabs-dashboard__panel">
            <TopLocationsPanel
              topLocations={data.topLocations}
              metricLabel={metricLabel}
              activeTab={activeTab}
            />
          </div>

          {/* Top right: Channel Performance */}
          <div className="web-analytics-tabs-dashboard__panel web-analytics-tabs-dashboard__panel--channels">
            <h3 className="demographics-subtitle demographics-subtitle--with-tooltip">
              <span>{channelTitle}</span>
              <DefinitionTooltip
                label={channelTitle}
                info={channelTooltip}
                formulaTitle={t("webAnalytics.kpi.formula")}
              />
            </h3>
            <div className="channel-list">
              {hasChannelValues ? plottableChannels.map(ch => (
                <ChannelRow
                  key={ch.name}
                  name={formatDimensionValue("channel", ch.name, t)}
                  value={ch.value}
                  changePercent={normalizeTrendPercent(ch.changePercent)}
                  trendData={ch.trendData}
                  maxValue={channelMaxValue}
                  minValue={channelMinValue}
                  metricLabel={metricLabel}
                  startMonthLabel={startMonthLabel}
                  endMonthLabel={endMonthLabel}
                  activeTab={activeTab}
                />
              )) : (
                <WebAnalyticsUnavailableState className="web-analytics-unavailable--channel" />
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Bottom: age / gender / device across the full card width */}
        <div
          className="web-analytics-tabs-dashboard__panel web-analytics-tabs-dashboard__demographics"
          data-pdf-block="demographics"
        >
          <div className="demographics-age">
            <DemographicChartTitle activeTab={activeTab} dimension="age" />
            {hasAgeValues ? (
              <AgeBarList ages={plottableAges} metricLabel={metricLabel} activeTab={activeTab} />
            ) : (
              <WebAnalyticsUnavailableState />
            )}
          </div>

          <div className="demographics-donut">
            <DemographicChartTitle activeTab={activeTab} dimension="gender" />
            {hasGenderValues ? (
              <DonutWithLegend
                data={plottableGender}
                TooltipComponent={PieTooltip}
                metricLabel={metricLabel}
                activeTab={activeTab}
                dimension="gender"
              />
            ) : (
              <WebAnalyticsUnavailableState />
            )}
          </div>

          <div className="demographics-donut">
            <DemographicChartTitle activeTab={activeTab} dimension="device" />
            {hasDeviceValues ? (
              <DonutWithLegend
                data={plottableDevice}
                TooltipComponent={DevicePieTooltip}
                metricLabel={metricLabel}
                activeTab={activeTab}
                dimension="device"
                legendClassName="donut-legend--device"
              />
            ) : (
              <WebAnalyticsUnavailableState />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabsDashboard;
